import { motion } from "framer-motion";
import { Check } from "lucide-react";

const plans = [
  {
    name: "Jednorazový tréning",
    price: "25",
    unit: "€ / hodina",
    description: "Ideálne na vyskúšanie alebo nepravidelné tréningy.",
    features: [
      "1 individuálny tréning",
      "Analýza tvojej kondície",
      "Cvičebný plán na hodinu",
      "Odborné vedenie trénera",
      "Poradenstvo po tréningu",
    ],
    cta: "Rezervovať tréning",
    highlighted: false,
  },
  {
    name: "Balíček tréningov",
    price: "210",
    unit: "€ / 10 tréningov",
    savings: "Ušetríš 40 €",
    priceNote: "21 € / tréning",
    description: "Najobľúbenejší balíček pre dlhodobé a viditeľné výsledky.",
    features: [
      "10 individuálnych tréningov",
      "Kompletná analýza tela",
      "Osobný tréningový plán",
      "Priebežné hodnotenie pokroku",
      "WhatsApp podpora",
    ],
    cta: "Začni teraz",
    highlighted: true,
    badge: "Najpopulárnejší",
  },
];

export default function Pricing() {
  const handleContact = (planName) => {
    const subject = encodeURIComponent(`Záujem o ${planName}`);
    const body = encodeURIComponent(
      `Dobrý deň Danka,\n\nMám záujem o: ${planName}\n\nProsím kontaktujte ma.\n\nĎakujem`
    );
    window.location.href = `mailto:danka.vysoka@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="cennik"
      className="py-24 lg:py-32 bg-[#1C1C2E] relative overflow-hidden"
    >
      {/* Top gradient */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#252540] to-transparent pointer-events-none" />

      {/* Decorative blob */}
      <div className="absolute top-1/2 right-0 w-80 h-80 bg-[#F72585]/8 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#F72585] block mb-3">
            Investícia do seba
          </span>
          <h2
            className="text-4xl lg:text-5xl font-black uppercase tracking-tight text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            CENNÍK
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
              data-testid={`pricing-card-${i}`}
              className="relative rounded-2xl p-8 flex flex-col"
              style={{
                background: "#252540",
                border: plan.highlighted
                  ? "2px solid #F72585"
                  : "1px solid rgba(255,255,255,0.1)",
                boxShadow: plan.highlighted
                  ? "0 0 50px rgba(247,37,133,0.15)"
                  : "none",
              }}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span
                    data-testid="pricing-popular-badge"
                    className="text-white text-xs font-bold px-5 py-2 rounded-full shadow-lg"
                    style={{
                      background:
                        "linear-gradient(90deg, #F72585, #7209B7)",
                    }}
                  >
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {plan.name}
                </h3>
                <p className="text-sm text-zinc-400">{plan.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-end gap-2">
                  <span
                    className="text-5xl font-black"
                    style={{
                      fontFamily: "Outfit, sans-serif",
                      color: plan.highlighted ? "#F72585" : "#ffffff",
                    }}
                  >
                    {plan.price}
                  </span>
                  <span className="text-zinc-400 mb-2 text-sm">
                    {plan.unit}
                  </span>
                </div>
                {plan.priceNote && (
                  <div className="text-sm text-zinc-400 mt-1">
                    {plan.priceNote}
                  </div>
                )}
                {plan.savings && (
                  <span className="inline-block mt-2 text-xs font-semibold text-green-400 bg-green-400/10 rounded-full px-3 py-1">
                    {plan.savings}
                  </span>
                )}
              </div>

              <ul className="space-y-3 flex-grow mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check
                      size={16}
                      className={`mt-0.5 flex-shrink-0 ${
                        plan.highlighted ? "text-[#F72585]" : "text-zinc-500"
                      }`}
                    />
                    <span className="text-sm text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleContact(plan.name)}
                data-testid={`pricing-cta-${i}`}
                className="w-full py-4 rounded-full font-bold text-sm transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: plan.highlighted ? "#F72585" : "transparent",
                  color: "#ffffff",
                  border: plan.highlighted
                    ? "none"
                    : "2px solid rgba(255,255,255,0.2)",
                }}
                onMouseEnter={(e) => {
                  if (plan.highlighted) {
                    e.target.style.background = "#E01A4F";
                    e.target.style.boxShadow =
                      "0 0 30px rgba(247,37,133,0.4)";
                  } else {
                    e.target.style.borderColor = "#F72585";
                    e.target.style.color = "#F72585";
                  }
                }}
                onMouseLeave={(e) => {
                  if (plan.highlighted) {
                    e.target.style.background = "#F72585";
                    e.target.style.boxShadow = "none";
                  } else {
                    e.target.style.borderColor = "rgba(255,255,255,0.2)";
                    e.target.style.color = "#ffffff";
                  }
                }}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#252540] to-transparent pointer-events-none" />
    </section>
  );
}
